import { CONFIG, SELECTOR } from "./config.es6";
import Cards from "./cards.es6";
import View from "./view.es6";
import Share from "./share.es6";

export default class Shinkeisuijaku {
    constructor(root) {
        this.root = root;
        this.board = root.querySelector(SELECTOR.board);
        if (!this.board) return;

        this.cards = new Cards(this.board);
        this.view = new View(root);
        this.share = new Share(root);

        this.timerId = null;
        this.timeoutIds = new Set();
        this.runId = 0;
        this.state = "title";
        this.deck = [];
        this.firstCard = null;
        this.isLocked = true;
        this.isFinished = false;
        this.matchedPairs = 0;
        this.remainingTime = CONFIG.totalTime;
        this.startedAt = null;
        this.deadline = null;
        const searchParams = new URLSearchParams(window.location.search);
        this.debugResult = searchParams.get("debug-result");
        this.isDebugResult = this.debugResult === "success" || this.debugResult === "failure";
        this.isDebugPairLayout = searchParams.has("debug-pairs");

        this.handleBoardClick = this.handleBoardClick.bind(this);
        this.handleStartClick = this.handleStartClick.bind(this);
        this.handleShareClick = this.share.handleShareClick.bind(this.share);

        this.bindEvents();
        this.resetToTitle();
        if (this.isDebugResult) this.showDebugResult(this.debugResult === "success");
    }

    bindEvents() {
        this.board.addEventListener("click", this.handleBoardClick);
        this.root.querySelectorAll(SELECTOR.start).forEach((button) => {
            button.addEventListener("click", this.handleStartClick);
        });
        this.root.querySelectorAll(SELECTOR.share).forEach((link) => {
            link.addEventListener("click", this.handleShareClick);
        });
    }

    handleStartClick() {
        if (this.state === "ready" || this.state === "playing") return;
        this.startGame();
    }

    showDebugResult(isSuccess) {
        // レイアウト調整中にゲームを完了させなくても各結果を再現できるようにする。
        this.state = isSuccess ? "success" : "failure";
        this.isFinished = true;
        this.matchedPairs = isSuccess ? CONFIG.totalPairs : 0;
        this.remainingTime = 0;
        this.view.updateMatchedCount(this.matchedPairs);
        this.view.updateTimer(this.remainingTime);
        this.showResult(isSuccess, CONFIG.totalTime);
    }

    handleBoardClick(event) {
        const cardElement = event.target.closest(SELECTOR.card);
        if (!cardElement || !this.board.contains(cardElement)) return;
        this.selectCard(cardElement);
    }

    startGame() {
        this.cancelAsyncTasks();
        this.runId += 1;
        this.state = "ready";
        this.deck = this.cards.createDeck({ adjacentPairs: this.isDebugPairLayout });
        this.firstCard = null;
        this.isLocked = true;
        this.isFinished = false;
        this.matchedPairs = 0;
        this.remainingTime = CONFIG.totalTime;
        this.startedAt = null;
        this.deadline = null;

        this.cards.renderCards(this.deck);
        this.view.updateMatchedCount(this.matchedPairs);
        this.view.updateTimer(this.remainingTime);
        this.setBoardLocked(true);
        this.view.setScreen("game");
        this.view.setGameState("ready");
        this.playCountdown(this.runId);
    }

    async playCountdown(runId) {
        const overlay = this.root.querySelector(SELECTOR.countdown);
        const text = this.root.querySelector(SELECTOR.countdownText);
        if (overlay) overlay.classList.add("is-active");
        this.root.setAttribute("aria-busy", "true");

        for (const [index, item] of CONFIG.countdown.entries()) {
            if (runId !== this.runId) return;
            if (overlay) overlay.dataset.phase = item.phase;
            if (text) {
                text.src = `${CONFIG.effectImageDirectory}${item.image}`;
                text.alt = item.text;
                text.dataset.count = String(index + 1);
            }
            this.dispatch("countdown", item);
            await this.wait(item.duration, runId);
        }

        if (runId !== this.runId) return;
        if (overlay) {
            overlay.classList.remove("is-active");
            delete overlay.dataset.phase;
        }
        this.root.setAttribute("aria-busy", "false");
        this.beginTimer();
    }

    beginTimer() {
        if (this.timerId || this.isFinished || this.state !== "ready") return;

        this.state = "playing";
        this.startedAt = performance.now();
        this.deadline = this.startedAt + CONFIG.totalTime * 1000;
        this.setBoardLocked(false);
        this.view.setGameState("playing");
        this.view.updateTimer(this.remainingTime);
        this.timerId = window.setInterval(() => this.tick(), 100);
    }

    tick() {
        if (this.isFinished || this.deadline === null) return;
        this.remainingTime = Math.max(0, (this.deadline - performance.now()) / 1000);
        this.view.updateTimer(this.remainingTime);
        if (this.remainingTime <= 0) this.finish(false);
    }

    selectCard(cardElement) {
        if (this.state !== "playing" || this.isLocked || this.isFinished) return;
        if (cardElement.classList.contains("is-flipped") || cardElement.classList.contains("is-matched")) return;

        const cardIndex = Number(cardElement.dataset.cardIndex);
        if (!Number.isInteger(cardIndex) || !this.deck[cardIndex]) return;

        this.cards.openCard(cardElement);
        if (!this.firstCard) {
            this.firstCard = { element: cardElement, index: cardIndex };
            return;
        }

        const first = this.firstCard;
        this.firstCard = null;
        this.setBoardLocked(true);
        this.judgeCards(first, { element: cardElement, index: cardIndex });
    }

    judgeCards(first, second) {
        if (this.isFinished) return;
        const firstData = this.deck[first.index];
        const secondData = this.deck[second.index];
        const isPair = firstData.pair === secondData.pair && firstData.side !== secondData.side;

        if (isPair) {
            this.schedule(() => this.completePair(first.element, second.element), CONFIG.matchDelay);
            return;
        }

        this.schedule(() => {
            this.cards.closeCard(first.element);
            this.cards.closeCard(second.element);
            this.setBoardLocked(false);
        }, CONFIG.judgeDelay);
    }

    completePair(firstElement, secondElement) {
        [firstElement, secondElement].forEach((element) => {
            this.cards.matchCard(element);
        });
        this.matchedPairs += 1;
        this.view.updateMatchedCount(this.matchedPairs);
        this.showMatchEffect();
        this.dispatch("match", { matchedPairs: this.matchedPairs, totalPairs: CONFIG.totalPairs });

        if (this.matchedPairs === CONFIG.totalPairs) {
            this.finish(true);
            return;
        }
        this.setBoardLocked(false);
    }

    showMatchEffect() {
        const effect = this.root.querySelector(SELECTOR.matchEffect);
        if (!effect) return;
        effect.classList.remove("is-active");
        // 同じ要素で連続して演出するため、再描画を挟んでanimationを再始動する。
        void effect.offsetWidth;
        effect.classList.add("is-active");
        this.schedule(() => effect.classList.remove("is-active"), CONFIG.matchEffectDuration);
    }

    finish(isSuccess) {
        // 最後の成立と時間切れが同じtickで発生しても結果は一度だけ確定する。
        if (this.isFinished) return;
        this.isFinished = true;
        this.cancelAsyncTasks();
        this.runId += 1;
        this.state = isSuccess ? "success" : "failure";
        this.setBoardLocked(true);

        if (this.deadline !== null) {
            this.remainingTime = Math.max(0, (this.deadline - performance.now()) / 1000);
        }
        const elapsedSeconds = isSuccess ? Math.min(CONFIG.totalTime, Math.max(0, Math.ceil(CONFIG.totalTime - this.remainingTime))) : CONFIG.totalTime;
        this.view.updateTimer(this.remainingTime);

        // 勝敗で結果表示の開始タイミングがずれないよう、判定後は同じ待機時間を挟む。
        this.schedule(() => this.showResult(isSuccess, elapsedSeconds), CONFIG.resultDelay);
    }

    showResult(isSuccess, elapsedSeconds) {
        this.view.updateResult(isSuccess, elapsedSeconds, this.matchedPairs);
        const shareText = this.share.createShareText(isSuccess, elapsedSeconds, this.matchedPairs);
        this.share.updateShareLinks(shareText);
        this.view.setGameState(this.state);
        this.view.setScreen("result");
        this.dispatch("finish", {
            result: this.state,
            matchedPairs: this.matchedPairs,
            elapsedSeconds,
        });

        const heading = this.root.querySelector(SELECTOR.resultHeading);
        if (heading) {
            heading.setAttribute("tabindex", "-1");
            heading.focus({ preventScroll: true });
        }
    }

    resetToTitle() {
        this.cancelAsyncTasks();
        this.runId += 1;
        this.state = "title";
        this.deck = [];
        this.firstCard = null;
        this.isLocked = true;
        this.isFinished = false;
        this.matchedPairs = 0;
        this.remainingTime = CONFIG.totalTime;
        this.startedAt = null;
        this.deadline = null;
        this.board.innerHTML = "";
        this.setBoardLocked(true);
        this.view.updateMatchedCount(this.matchedPairs);
        this.view.updateTimer(this.remainingTime);
        this.view.setScreen("title");
        this.view.setGameState("title");

        const countdown = this.root.querySelector(SELECTOR.countdown);
        if (countdown) {
            countdown.classList.remove("is-active");
            delete countdown.dataset.phase;
        }
        const countdownText = this.root.querySelector(SELECTOR.countdownText);
        if (countdownText) delete countdownText.dataset.count;
        const effect = this.root.querySelector(SELECTOR.matchEffect);
        if (effect) effect.classList.remove("is-active");
        this.root.setAttribute("aria-busy", "false");
    }

    setBoardLocked(isLocked) {
        this.isLocked = isLocked;
        this.board.classList.toggle("is-locked", isLocked);
        this.board.setAttribute("aria-busy", String(isLocked));
    }

    wait(duration, runId) {
        return new Promise((resolve) => {
            const id = window.setTimeout(() => {
                this.timeoutIds.delete(id);
                resolve(runId === this.runId);
            }, duration);
            this.timeoutIds.add(id);
        });
    }

    schedule(callback, duration) {
        const runId = this.runId;
        const id = window.setTimeout(() => {
            this.timeoutIds.delete(id);
            if (runId === this.runId) callback();
        }, duration);
        this.timeoutIds.add(id);
    }

    stopTimer() {
        if (!this.timerId) return;
        window.clearInterval(this.timerId);
        this.timerId = null;
    }

    cancelAsyncTasks() {
        this.stopTimer();
        this.timeoutIds.forEach((id) => window.clearTimeout(id));
        this.timeoutIds.clear();
    }

    dispatch(name, detail) {
        this.root.dispatchEvent(new CustomEvent(`shinkeisuijaku:${name}`, { detail }));
    }
}
